import { Heading, Text } from '@chakra-ui/layout'
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    IconButton
} from '@chakra-ui/react'
import { Icon, Layout } from 'components/atoms'
import { InputBuild } from 'components/molecules/'
import { useEffect, useMemo, useState } from 'react'
import { FieldError, useFieldArray, useFormContext } from 'react-hook-form'
import formatter from 'utils/formatter'
import { ResponsibleForm } from './form'
import { ResponsibleFormProps } from './interfaces'
import { Container, Header } from './styles'
import { ResponsibleKeys } from './types'

const Responsible: React.FC = () => {
    const formDefault = {
        nome: '',
        cpf: '',
        contato: '',
        telefone: '',
        funcao: '',
        email: ''
    }
    const {
        control,
        register,
        watch,
        formState: { errors }
    } = useFormContext<{ responsible: ResponsibleFormProps }>()

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'responsible.form'
    })

    const form = useMemo(() => ResponsibleForm, [ResponsibleForm])
    const fieldsArray = useMemo(() => fields, [fields])

    const hasError = (
        index: number,
        id: ResponsibleKeys
    ): FieldError | undefined => {
        const err = errors.responsible?.form?.[index]
        return err && err[id]
    }

    const [accordionIndex, setAccordionIndex] = useState(0)
    const handleChange = (index: number): void => {
        setAccordionIndex(index)
    }
    const handleAdd = (): void => {
        append(formDefault)
        setAccordionIndex(fields.length)
    }
    const handleDelete = (index: number): void => {
        remove(index)
    }

    useEffect(() => {
        !fields.length && handleAdd()
    }, [])

    return (
        <Container
            data-testid="container"
            full
            type="container"
            px={8}
            display="grid"
            gap={8}
        >
            <Header type="row">
                <Box position="relative">
                    <Heading
                        display="flex"
                        size="md"
                        alignItems="center"
                        justifyContent={{
                            base: 'space-between',
                            sm: 'center'
                        }}
                        minH="32px"
                    >
                        Responsáveis legais
                        <IconButton
                            data-testid="iconButton"
                            position="absolute"
                            right="1rem"
                            colorScheme="primary"
                            aria-label="Search database"
                            icon={<Icon name="add" />}
                            onClick={handleAdd}
                        />
                    </Heading>
                </Box>
            </Header>
            <Accordion
                data-testid="accordion"
                defaultValue={accordionIndex}
                index={accordionIndex}
                allowToggle
                onChange={handleChange}
            >
                {fieldsArray.map((item, index) => (
                    <AccordionItem key={item.id}>
                        <h2>
                            <AccordionButton px={0}>
                                <Box flex="1">
                                    <Text
                                        textAlign="left"
                                        textTransform="capitalize"
                                        fontWeight="bold"
                                    >
                                        Responsável legal:{' '}
                                        {formatter.firstLatterUppercase(
                                            watch(
                                                `responsible.form.${index}.nome`
                                            )
                                        ) || index + 1}
                                    </Text>
                                </Box>
                                {fields.length > 1 && (
                                    <Icon
                                        data-testid={`iconDelete.${index}`}
                                        name="delet"
                                        onClick={() => handleDelete(index)}
                                    />
                                )}

                                <AccordionIcon />
                            </AccordionButton>
                        </h2>
                        <AccordionPanel px={0} pb={4}>
                            <Layout type="row" gutter={{ y: 1.5 }} mb={6}>
                                {form.map(({ layout, input }, key) => (
                                    <Layout {...layout} key={key}>
                                        <InputBuild
                                            {...input}
                                            id={`${index}.${input.id}`}
                                            err={hasError(index, input.id)}
                                            {...register(
                                                `responsible.form.${index}.${input.id}`
                                            )}
                                            value={watch(
                                                `responsible.form.${index}.${input.id}`
                                            )}
                                        />
                                    </Layout>
                                ))}
                            </Layout>
                        </AccordionPanel>
                    </AccordionItem>
                ))}
            </Accordion>
        </Container>
    )
}

export default Responsible
